import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import {
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../stores/notificationStore';

const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, getDisplayName, getInitials, isAdmin } = useAuth();
  const { success: showSuccess } = useNotifications();

  const handleLogout = async () => {
    try {
      await logout();
      showSuccess('Logout realizado com sucesso');
      navigate('/login');
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  const handleProfile = () => {
    navigate('/profile');
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  if (!user) {
    return null;
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <div>
            <Menu.Button className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 p-2 hover:bg-gray-50 transition-colors">
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-brand-primary)' }}>
                <span className="text-white text-sm font-medium">
                  {getInitials()}
                </span>
              </div>

              {/* Nome e Info */}
              <div className="hidden md:block text-left">
                <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {getDisplayName()}
                </div>
                <div className="text-xs">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100" style={{ color: 'var(--color-text-secondary)' }}>
                    {isAdmin() ? 'Admin' : 'Operador'}
                  </span>
                </div>
              </div>
              
              {/* Chevron */}
              <ChevronDownIcon
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                {/* Informações do usuário */}
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-brand-primary)' }}>
                      <span className="text-white font-medium">
                        {getInitials()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {getDisplayName()}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  
                  {/* Status/Badge */}
                  <div className="mt-2 flex items-center">
                    <div className={`
                      inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                      ${user.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                      }
                    `}>
                      <div className={`
                        w-1.5 h-1.5 rounded-full mr-1
                        ${user.is_active ? 'bg-green-400' : 'bg-red-400'}
                      `} />
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </div>
                    
                    {isAdmin() && (
                      <div className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        Admin
                      </div>
                    )}
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleProfile}
                        className={`
                          group flex items-center w-full px-4 py-2 text-sm
                          ${active 
                            ? 'bg-gray-100 text-gray-900' 
                            : 'text-gray-700'
                          }
                        `}
                      >
                        <UserIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                        Meu Perfil
                      </button>
                    )}
                  </Menu.Item>

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleSettings}
                        className={`
                          group flex items-center w-full px-4 py-2 text-sm
                          ${active 
                            ? 'bg-gray-100 text-gray-900' 
                            : 'text-gray-700'
                          }
                        `}
                      >
                        <Cog6ToothIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                        Configurações
                      </button>
                    )}
                  </Menu.Item>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200" />

                {/* Logout */}
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`
                          group flex items-center w-full px-4 py-2 text-sm
                          ${active 
                            ? 'bg-red-50 text-red-900' 
                            : 'text-red-700'
                          }
                        `}
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-red-400 group-hover:text-red-500" />
                        Sair
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default UserMenu;